import store from "@/store"
import config from "../../config.json"
import http from "./http-service"
import CustomerService from "./customer-service"
import { diff } from 'json-diff'
import { AppBrowser, colors, numeral } from "@/utils"

export default class QuotationService {
  
  static getById(id, revision, impostos) {

    let url = `quotations/${id}`

    if (revision)
      url += `?revision=${revision}`
    
    if (impostos)
      url += `?impostos=${impostos}`

    return http.get(url).then(async (quotation) => {
      quotation.reserves = ""
      quotation.statusData = QuotationService.QUOTATION_STATUS[quotation.status]
      quotation.isOwner = (store.state.user.id == quotation.idUser)
      quotation.isApprover = (store.state.user.id == quotation.idApprover)

      quotation.allowUpdate = false

      if (quotation.isApprover && quotation.approvingly) {

        quotation.allowUpdate = true

      } else if (quotation.isOwner) {

        if (quotation.underMaintenance || quotation.waitingApproval || quotation.waitingConfirm || quotation.tecnicalReject ||
            quotation.waitingProcessing || quotation.errorProcessing || quotation.notApproved) {
          quotation.allowUpdate = true
        }
      }

      if (quotation.isRevision)
        quotation.statusData.description += " " + quotation.lastRevision

      quotation.items.forEach((item) => {

        QuotationService.updateItemData(item)

        if (item.reserveId) {
          if (quotation.reserves)
            quotation += ", "
          quotation.reserves += item.reserveId
        }
      })

      QuotationService.updateTotals(quotation)

      await QuotationService.loadMinPriceDiscounts(quotation)

      return quotation
    })
  }

  static async loadMinPriceDiscounts(quotation) {
    // Mod-Perf A2: uma unica chamada batch (read-only) em vez de N get-product-price
    // paralelos. Antes, abrir um orcamento disparava N POSTs simultaneos (um por item),
    // saturando o pool de threads do Protheus (acesso ~32s + erros 503). O endpoint
    // recalc-batch ja itera os itens no backend reaproveitando o vendedor/total resolvidos
    // uma unica vez e (Mod-Perf A1) o cache de carta.
    //
    // IMPORTANTE: aqui aplicamos APENAS minPriceDiscount (Desc. Maximo) e stdPrice.
    // NAO chamamos applyPriceData -> nao alteramos appPrice/sugPrice/aprovacao. O backend
    // calcItemBatch e somente leitura (nao grava ZP6). minPriceDiscountRange/standardPrice
    // sao calculados sobre o nMinPrice/carta, independentes do preco avaliado no item.
    if (!quotation.items || quotation.items.length === 0)
      return

    const { codeCustomer, unitCustomer, paymentCondition, freight, isRetail } = quotation

    const payload = {
      codeCustomer,
      unitCustomer,
      paymentCondition,
      freight,
      isRetail,
      userId: quotation.idUser,
      items: quotation.items,
    }

    try {
      const response = await http.post("quotations/recalc-batch", payload)
      const results = (response && response.items) || []
      // mesma ordem do payload (o backend devolve por indice)
      quotation.items.forEach((item, idx) => {
        const priceData = results[idx]
        if (!priceData || priceData.ok === false) {
          if (!item.stdPrice) item.stdPrice = item.minPrice
          return
        }
        item.minPriceDiscount = priceData.minPriceDiscountRange || priceData.effectiveDiscountRange || ''
        item.stdPrice = priceData.standardPrice || item.minPrice

        // Erro22136/v5 (recompute-on-read): recompoe o tipificado do sugerido no formato
        // novo (slots fixos P|A|V|F|O), corrigindo a exibicao de orcamentos gravados com a
        // estrutura antiga. SOMENTE exibicao: nao altera appPrice/sugPrice/aprovacao.
        if (priceData.discountTyped) {
          item.sugDiscTyped = priceData.discountTyped
          const parsed = QuotationService.parseDiscountTyped(priceData.discountTyped)
          if (parsed && parsed.length > 0)
            item.sugDiscount = parsed.filter((p) => Number(p.value) !== 0).map((p) => p.value).join('+')
          // auto-aprovado (aprovado == sugerido) -> o aprovado usa o mesmo tipificado
          if (Number(item.appPrice) > 0 && Math.abs(Number(item.appPrice) - Number(item.sugPrice)) < 0.001)
            item.appDiscTyped = priceData.discountTyped
        }
      })
    } catch (e) {
      // Enriquecimento de exibicao: nunca pode quebrar o carregamento do orcamento.
      quotation.items.forEach((item) => {
        if (!item.minPriceDiscount) item.minPriceDiscount = ''
        if (!item.stdPrice) item.stdPrice = item.minPrice
      })
    }
  }

  static async getByIdDif(id, revision) {
    let ret = null
    let revant = null
    let lastRev = null
    let AntRev = null

    revant = (revision-1)
    if (revant < 10) {
      revant = '0' + revant
    }

    lastRev = await QuotationService.getById(id, revision)
    AntRev = await QuotationService.getById(id, revant)
    ret = compareRev(AntRev, lastRev)
    
    return ret
  }

  static updateItemData(item) {

    item.description = QuotationService.getItemDescription(item)
    item.status = QuotationService.itemStatus(item)        
    item.sugPriceAlert = QuotationService.hasSugPriceAlert(item)
    item.appPriceAlert = QuotationService.hasAppPriceAlert(item)
    item.packAlert = QuotationService.hasPackAlert(item)
    item.colorAlert = QuotationService.hasColorAlert(item)
    item.appPriceData = QuotationService.appPriceData(item)
    item.quantityAlert = (item.packAlert || item.colorAlert)
    item.quantityPopupHtml = QuotationService.itemQuantityPopupHtml(item)
  }

  static updateTotals(quotation) {

    quotation.sugTotal = 0
    quotation.appTotal = 0
    quotation.totalMeters = 0
    quotation.totalFlex = 0
    quotation.flexPerc = 0
    quotation.hasFlexAlert = false

    quotation.items.forEach((item) => {

      quotation.sugTotal += item.sugTotal
      quotation.appTotal += item.appTotal
      quotation.totalMeters += item.quantity

      if (item.codeName.trim() == "115" && item.codeBitola.trim() == "05")
        quotation.totalFlex += item.quantity 
    })     

    if (quotation.totalFlex > 0)
      quotation.flexPerc = ((quotation.totalFlex / quotation.totalMeters) * 100)

    quotation.hasFlexAlert = (quotation.flexPerc > 25)  

    return quotation
  }

  static calculateSugPrice(quotation, item, sugPrice) {

    const { codeCustomer, unitCustomer, paymentCondition, freight, isRetail } = quotation
    let userId = store.state.user.id

    if (CustomerService.isDefaultCustomer(quotation.customer))
      userId = quotation.idUser

    // Erro01: usar quantity*sugPrice como fallback quando sugTotal ainda nao calculado.
    // Sem isso o backend recebe totalPedido=0 e qryFaixa retorna apenas a faixa 1.
    let totalPedido = 0
    if (quotation.items && quotation.items.length > 0) {
      quotation.items.forEach((i) => {
        totalPedido += (i.sugTotal || ((Number(i.quantity) || 0) * (Number(i.sugPrice) || 0)) || 0)
      })
    }
    // Incluir item atual se ainda nao esta no array (ex: primeiro item sendo inserido)
    const itemTotalEstimate = item.sugTotal || ((Number(item.quantity) || 0) * (Number(sugPrice) || Number(item.sugPrice) || 0)) || 0
    if (itemTotalEstimate && (!quotation.items || quotation.items.indexOf(item) === -1)) {
      totalPedido += itemTotalEstimate
    }

    let data = {
      codeCustomer,
      unitCustomer,
      paymentCondition,
      freight,
      isRetail,
      item,
      userId,
      unitPrice: sugPrice,
      totalPedido,
    }

    return QuotationService.getProductPrice(data)
      .then((priceData) => QuotationService.applyPriceData(item, priceData, sugPrice))
  }

  // Mod001 Performance: extraido do .then() de calculateSugPrice para ser reutilizado pelo recalcBatch.
  static applyPriceData(item, priceData, sugPrice) {

    // Erro21397v2/v4: estado anterior para preservar aprovacao manual quando o PRECO sugerido nao muda
    const oldAppPrice = Number(item.appPrice) || 0
    const oldSugPrice = Number(item.sugPrice) || 0
    const wasManualApproval = (oldAppPrice > 0 && !item.appAuto)

    if (!item.quantity)
      item.quantity = 0

    if (!item.appPrice)
      item.appPrice = 0

    item.listPriceId = priceData.listPriceId
    item.listPrice = priceData.listPrice
    item.cashPaymentDiscount = priceData.cashPaymentDiscount
    item.freightDiscount = priceData.freightDiscount
    item.minPrice = priceData.minPrice
    item.minRg = priceData.minRg
    item.minPriceDiscount = priceData.minPriceDiscountRange || ''
    item.discountCard = priceData.discountCard || ''
    item.discountAdditional = priceData.discountAdditional || ''
    item.discountExtra = priceData.discountExtra || ''
    item.stdPrice = priceData.standardPrice || item.minPrice

    item.defDiscount = priceData.defaultDiscountRange

    if (sugPrice) {

      item.sugPrice = sugPrice
      item.sugRg = priceData.unitPriceRg

      // Mod20634-1 / Erro22136-v5: sempre exibir desconto. Comportamento:
      //  sugPrice >  stdPrice  -> desconto efetivo numerico (0 quando == listPrice, negativo se acima)
      //  sugPrice <= stdPrice  -> tipificado canonico do backend (slots fixos P|A|V|F|O)
      if (item.stdPrice && Number(sugPrice) > Number(item.stdPrice)) {
        // sugPrice acima do padrao -> desconto efetivo numerico (0 == listPrice, negativo se acima)
        const lp = Number(item.listPrice) || 0
        const eff = lp > 0 ? ((lp - Number(sugPrice)) / lp) * 100 : 0
        const effRounded = Math.round(eff * 100) / 100
        item.sugDiscount = String(effRounded)
        item.sugDiscTyped = 'F:' + effRounded
        if (Number(sugPrice) >= Number(item.listPrice)) {
          item.discountCard = ""
          item.discountAdditional = ""
          item.discountExtra = ""
        }
      } else {
        // Erro22136/v5: usa o tipificado canonico do backend (slots fixos P|A|V|F|O).
        // Substitui a reconstrucao posicional [P,A,V]+F/O, que perdia a faixa e fragmentava.
        item.sugDiscTyped = priceData.discountTyped || ''
        const parsed = QuotationService.parseDiscountTyped(item.sugDiscTyped)
        item.sugDiscount = (parsed && parsed.length > 0)
          ? parsed.filter(p => Number(p.value) !== 0).map(p => p.value).join('+')
          : (item.defDiscount || '')
      }

    } else {

      item.sugPrice = item.stdPrice || item.minPrice
      item.sugRg = item.minRg

      // Desconto sugerido = defDiscount (P+A+V fixos, sem Faixa)
      item.sugDiscount = item.defDiscount || ''
      // Reconstruir typed a partir de defDiscount
      const defParts = (item.defDiscount || '').split('+')
      let pavTyped = []
      if (defParts[0]) pavTyped.push('P:' + defParts[0])
      if (defParts[1]) pavTyped.push('A:' + defParts[1])
      if (defParts[2]) pavTyped.push('V:' + defParts[2])
      item.sugDiscTyped = pavTyped.join('|')
    }

    item.sugTotal = (item.quantity * item.sugPrice)
    item.sugCommission = priceData.commission
    item.priceDiff = 0

    // Erro08: tolerancia 0.001 absorve diferencas de arredondamento entre cumulativo e step-by-step (vide Mod20634)
    if (Number(item.sugPrice) + 0.001 < Number(item.minPrice)) {

      // Erro21397v4: se o PRECO SUGERIDO nao mudou e o item ja tinha aprovacao manual
      // (appPrice>0, nao-auto), PRESERVA a aprovacao. O aprovador aprovou um PRECO; recalculo
      // por edicao de cores ou troca de cliente (minPrice pode mudar) nao deve desfazer isso.
      const sugPriceChanged = Math.abs(oldSugPrice - Number(item.sugPrice)) > 0.001

      if (wasManualApproval && !sugPriceChanged) {
        item.appTotal = (item.quantity * item.appPrice)
        item.appAuto = false
      } else {
        item.appPrice = 0
        item.appTotal = 0
        item.appRg = 0
        item.appCommission = 0
        item.appDiscount = ""
        item.appDiscTyped = ""
        item.appAuto = false
      }

    } else {

      item.appPrice = item.sugPrice
      item.appTotal = item.sugTotal
      item.appRg = item.sugRg
      item.appCommission = item.sugCommission
      item.appDiscount = item.sugDiscount
      item.appDiscTyped = item.sugDiscTyped
      item.appAuto = true
    }

    QuotationService.updateItemData(item)

    return item
  }

  // Mod001 Performance: recalcula todos os itens da cotacao em uma unica chamada HTTP.
  // Substitui o loop sequencial (N+1 roundtrips) por um unico POST que itera no backend ADVPL.
  static recalcBatch(quotation) {

    if (!quotation.items || quotation.items.length === 0)
      return Promise.resolve([])

    const { codeCustomer, unitCustomer, paymentCondition, freight, isRetail } = quotation
    let userId = store.state.user.id

    if (CustomerService.isDefaultCustomer(quotation.customer))
      userId = quotation.idUser

    const payload = {
      codeCustomer,
      unitCustomer,
      paymentCondition,
      freight,
      isRetail,
      userId,
      items: quotation.items,
    }

    return http.post("quotations/recalc-batch", payload).then((response) => {
      const results = (response && response.items) || []

      // Se algum item nao puder ser calculado (tabela de preco fora de vigencia, etc.),
      // preserva o comportamento original (que falhava na primeira chamada per-item).
      const firstError = results.find((r) => r && r.ok === false)
      if (firstError) {
        const err = new Error(firstError.error || "Falha no recalculo de itens")
        err.apiError = true
        throw err
      }

      // Aplica cada priceData ao item correspondente (mesma ordem do payload).
      quotation.items.forEach((item, idx) => {
        const priceData = results[idx]
        if (!priceData) return
        QuotationService.applyPriceData(item, priceData, item.sugPrice)
      })
      return quotation.items
    })
  }

  static calculateAppPrice(quotation, item, appPrice) {

    const { codeCustomer, unitCustomer, paymentCondition, freight, isRetail } = quotation
    let userId = store.state.user.id

    if (CustomerService.isDefaultCustomer(quotation.customer))
      userId = quotation.idUser

    // Erro01: usar quantity*appPrice (ou sugPrice) como fallback quando appTotal ainda nao calculado.
    let totalPedido = 0
    if (quotation.items && quotation.items.length > 0) {
      quotation.items.forEach((i) => {
        totalPedido += (i.appTotal || ((Number(i.quantity) || 0) * (Number(i.appPrice) || Number(i.sugPrice) || 0)) || 0)
      })
    }
    const itemTotalEstimate = item.appTotal || ((Number(item.quantity) || 0) * (Number(appPrice) || Number(item.appPrice) || Number(item.sugPrice) || 0)) || 0
    if (itemTotalEstimate && (!quotation.items || quotation.items.indexOf(item) === -1)) {
      totalPedido += itemTotalEstimate
    }

    let data = {
      codeCustomer,
      unitCustomer,
      paymentCondition,
      freight,
      isRetail,
      item,
      userId,
      unitPrice: appPrice,
      totalPedido,
    }

    return QuotationService.getProductPrice(data)
      .then((priceData) => {

        if (!item.quantity)
          item.quantity = 0

        item.listPriceId = priceData.listPriceId
        item.listPrice = priceData.listPrice
        // Nao sobrescrever defDiscount no calculo de preco aprovado
        item.cashPaymentDiscount = priceData.cashPaymentDiscount
        item.freightDiscount = priceData.freightDiscount
        item.minPrice = priceData.minPrice
        item.minRg = priceData.minRg
        item.minPriceDiscount = priceData.minPriceDiscountRange || ''
        item.discountCard = priceData.discountCard || ''
        item.discountAdditional = priceData.discountAdditional || ''
        item.discountExtra = priceData.discountExtra || ''
        item.stdPrice = priceData.standardPrice || item.minPrice
        item.appPrice = appPrice
        item.appTotal = (item.quantity * item.appPrice)
        item.appRg = priceData.unitPriceRg
        item.appAuto = false

        // Mod20634-1: sempre exibir desconto aprovado
        if (item.stdPrice && Number(appPrice) > Number(item.stdPrice)) {
          const lp = Number(item.listPrice) || 0
          const eff = lp > 0 ? ((lp - Number(appPrice)) / lp) * 100 : 0
          const effRounded = Math.round(eff * 100) / 100
          item.appDiscount = String(effRounded)
          item.appDiscTyped = 'F:' + effRounded
        } else {
          // Erro22136/v5: tipificado canonico do backend (slots fixos P|A|V|F|O) p/ o aprovado
          item.appDiscTyped = priceData.discountTyped || ''
          const parsed = QuotationService.parseDiscountTyped(item.appDiscTyped)
          item.appDiscount = (parsed && parsed.length > 0)
            ? parsed.filter((p) => Number(p.value) !== 0).map((p) => p.value).join('+')
            : (item.defDiscount || '')
        }

        if (!item.isFixedCommission)
          item.appCommission = priceData.commission

        if (!item.sugPrice || item.sugPrice == 0 || item.priceSugByApp) {
          item.sugPrice = item.appPrice
          item.sugTotal = item.appTotal
          item.sugRg = item.appRg
          item.sugCommission = item.appCommission
          item.sugDiscount = item.appDiscount
          item.sugDiscTyped = item.appDiscTyped
          item.priceSugByApp = true
        }

        if (item.appPrice == 0 && !item.isFixedCommission)
          item.appCommission = 0

        item.priceDiff = QuotationService.getPriceDiff(item.sugPrice, item.appPrice)
        
        QuotationService.updateItemData(item)

        return item
      })
  }

  static getPriceDiff(sugPrice, appPrice) {

    if (!appPrice || appPrice == 0 || !sugPrice || sugPrice == 0)
      return 0

    let diffValue = (appPrice - sugPrice)

    if (diffValue == 0)
      return 0

    let diffPerc = ((diffValue / sugPrice) * 100)

    return diffPerc
  }

  static getItemDescription(item) {	

    let description = ""

    description =  item.name.trim() + " " 
    description += item.bitola.trim() + " "
    description += item.package.trim().toUpperCase() + " "
    description += item.amountPackage + " MT"

    return description
  }

  static hasPriceAlert(item) {
    return (QuotationService.hasSugPriceAlert(item) || QuotationService.hasAppPriceAlert(item))
  }

  static hasSugPriceAlert(item) {
    // Erro08: tolerancia 0.001 absorve diferencas de arredondamento (vide Mod20634)
    return (Number(item.sugPrice.toFixed(4)) + 0.001 < Number(item.minPrice.toFixed(4)))
  }

  static hasAppPriceAlert(item) {
    return (Number(item.appPrice.toFixed(4)) + 0.001 < Number(item.minPrice.toFixed(4)))
  }

  static hasPackAlert(item) {
    if (item.idReserve) {
      return false
    }
    else {
      return (item.isSpecificPackageAmount || item.specialty !== "01")
    }
  }

  static hasColorAlert(item) {

    let hasAlert = false

    item.colors.forEach((color) => {
      if (color.requireApproval)
        hasAlert = true
    })

    return hasAlert
  }

  static hasItemAlert(item) {
    return (QuotationService.hasPackAlert(item) || QuotationService.hasColorAlert(item) || QuotationService.hasPriceAlert(item))
  }

  static itemStatus(item) {

    let isOk = true
    let color = ""
    let icon = ""
    let label = ""
    let violatedRules = ""

    if (QuotationService.hasPriceAlert(item))
      violatedRules = "Preço"

    if (QuotationService.hasPackAlert(item)) {
      if (violatedRules)
        violatedRules += ", "
      violatedRules += "Acondicionamento"
    }

    if (QuotationService.hasColorAlert(item)) {
      if (violatedRules)
        violatedRules += ", "
      violatedRules += "Cores"
    }	

    if (violatedRules) {
      isOk = false
      color = "red"
      icon = "exclamation circle icon"
      label = "Regras violadas: " + violatedRules
    } else {
      color = "green"
      icon = "check circle icon"
      label = "Nenhuma regra violada"
    }

    const data = {
      isOk,	
      color,
      icon,
      label,
      html: `<i class="${icon} ${color} clickable quotation-item-icon" data-content="${label}" data-variation="inverted"></i>`,
    }

    return data
  }

  static appPriceData(item) {

    let data = {}

    if (item.appPrice == 0) {

      data.color = "red"
      data.icon = "ban icon"
      data.label = "Não aprovado"
    } else if (Number(item.appPrice.toFixed(4)) + 0.001 < Number(item.minPrice.toFixed(4))) {
      // Erro08: tolerancia 0.001 absorve diferencas de arredondamento (vide Mod20634)
      data.color = "orange"
      data.icon = "exclamation triangle icon"
      data.label = "Aprovado abaixo do Preço Mínimo"
    } else if (Number(item.appPrice.toFixed(4)) > Number(item.sugPrice.toFixed(4))) {

      data.color = "teal"
      data.icon = "exclamation circle icon"
      data.label = "Aprovado acima do Preço Sugerido"

    } else {

      data.color = "green"
      data.icon = "check circle icon"
      data.label = "Preço Aprovado"
    }

    return data
  }

  static itemQuantityPopupHtml(item) {

    let html = ""
    let packColor = "black"
    let specialtyColor = "black"

    if (item.isSpecificPackageAmount)
      packColor = "red"

    if (item.specialty != "01")
      specialtyColor = "red"

    html += `<div class="${packColor} text"><strong>Metragem: </strong> ${formatInteger(item.amountPackage)}</div>`
    html += `<div class="${specialtyColor} text m-t-5 m-b-10"><strong>Especialidade: </strong> ${item.descSpecialty}</div>`

    item.colors.forEach((color) => {

      let itemColor = "black"

      if (color.requireApproval)
        itemColor = "red"

        html += `<div class="${itemColor} text m-t-5"><strong>${color.colorName.trim()}: </strong> ${formatInteger(color.total)}</div>`
    })

    return html
  }

  static formatDiscountBold(discountCard, discountAdditional, discountExtra) {
    let result = ''

    // Carta/faixa - texto normal
    if (discountCard) {
      result = discountCard
    }

    // Adicional - negrito
    if (discountAdditional) {
      if (result) result += '+'
      result += '<strong>' + discountAdditional + '</strong>'
    }

    // Fora - vermelho negrito
    if (discountExtra) {
      if (result) result += '+'
      result += '<strong style="color:red">' + discountExtra + '</strong>'
    }

    return result
  }

  static formatDiscountWithDef(fullDiscount, defDiscount) {
    if (!fullDiscount) return ''
    if (!defDiscount) return fullDiscount

    const fullParts = fullDiscount.split('+')
    const defParts = defDiscount.split('+')

    if (fullParts.length <= defParts.length) return fullDiscount

    // Prefix: partes iguais ao defDiscount (desconto padrao da carta)
    let prefixLen = 0
    while (prefixLen < defParts.length && defParts[prefixLen] === fullParts[prefixLen]) {
      prefixLen++
    }

    // Partes alem do defDiscount: faixa (bold) ou fora da carta (vermelho)
    // Fora da carta = ultima parte com decimal (ex: "23.28")
    const lastIdx = fullParts.length - 1
    const lastIsExtra = (lastIdx >= prefixLen) && fullParts[lastIdx].includes('.')

    return fullParts.map((part, i) => {
      if (i < prefixLen) return part
      if (i === lastIdx && lastIsExtra) {
        return '<strong style="color:red">' + part + '</strong>'
      }
      if (i >= prefixLen) {
        return '<strong>' + part + '</strong>'
      }
      return part
    }).join('+')
  }

  static parseDiscountTyped(typed) {
    if (!typed) return null
    const parts = typed.split('|')
    return parts.map(part => {
      const colonIdx = part.indexOf(':')
      if (colonIdx === -1) return { type: 'P', value: part }
      return { type: part.substring(0, colonIdx), value: part.substring(colonIdx + 1) }
    })
  }

  static formatDiscountFromTyped(typed) {
    const parts = QuotationService.parseDiscountTyped(typed)
    if (!parts || parts.length === 0) return ''
    return parts
      .filter(part => Number(part.value) !== 0)   // Erro22136/v5: ignora slots zerados (ex A:0)
      .map(part => {
        if (part.type === 'A') {
          return '<strong>' + part.value + '</strong>'
        }
        if (part.type === 'O') {
          return '<strong style="color:red">' + part.value + '</strong>'
        }
        return part.value
      }).join('+')
  }

  static rebuildSugTyped(defDiscount, backendTyped) {
    if (!backendTyped || !defDiscount) return backendTyped || ''
    const parts = QuotationService.parseDiscountTyped(backendTyped)
    if (!parts || parts.length === 0) return backendTyped

    // F e O do backend (variaveis)
    const foParts = parts.filter(p => p.type === 'F' || p.type === 'O')

    // P+A+V do defDiscount (fixos)
    const defParts = defDiscount.split('+')
    let pavTyped = []
    if (defParts[0]) pavTyped.push('P:' + defParts[0])
    if (defParts[1]) pavTyped.push('A:' + defParts[1])
    if (defParts[2]) pavTyped.push('V:' + defParts[2])

    return pavTyped.concat(foParts.map(p => p.type + ':' + p.value)).join('|')
  }

  static formatStdDiscount(typed) {
    const parts = QuotationService.parseDiscountTyped(typed)
    if (!parts || parts.length === 0) return ''
    return parts
      .filter(part => (part.type === 'P' || part.type === 'A' || part.type === 'V') && Number(part.value) !== 0)
      .map(part => part.value)
      .join('+')
  }

  static calculateStdPriceFromTyped(listPrice, typed) {
    if (!listPrice || !typed) return listPrice || 0
    const parts = QuotationService.parseDiscountTyped(typed)
    if (!parts || parts.length === 0) return listPrice
    let price = listPrice
    parts.forEach(part => {
      if (part.type === 'P' || part.type === 'A' || part.type === 'V') {
        price = price * (1 - parseFloat(part.value) / 100)
      }
    })
    return Math.round(price * 10000) / 10000
  }

  static canSeeRg() {

    const user = store.state.user

    return (user.isAdministrator || user.isSupervisor || user.isManager || user.isDirector)
  }

  static getEntityList(entity) {
    return http.get(`quotations/entity-list/${entity}`).then((response) => response.list)
  }

  static getPaymentConditions(quotationType, codeCustomer, unitCustomer) {

    let url = `payment-conditions?quotationType=${quotationType}&`
    
    url += `codeCustomer=${codeCustomer}&unitCustomer=${unitCustomer}`

    return http.get(url).then((response) => {
      QuotationService._blockedPartialBillingConditions = response.blockedPartialBillingConditions || ''
      return response.conditions
    })
  }

  static getBlockedPartialBillingConditions() {
    return QuotationService._blockedPartialBillingConditions || ''
  }

  static isPartialBillingBlocked(paymentCondition) {
    const blocked = QuotationService.getBlockedPartialBillingConditions()
    if (!blocked || !paymentCondition) return false
    return blocked.includes(paymentCondition.trim())
  }

  static getProductFamilies(codeCustomer, unitCustomer, isRetail) {

    let url = `product-families?codeCustomer=${codeCustomer}&unitCustomer=${unitCustomer}&isRetail=${isRetail}`

    return http.get(url).then((response) => response.families)
  }

  static getBitolas(codeCustomer, unitCustomer, family, isRetail) {

    let url = `bitolas?codeCustomer=${codeCustomer}&unitCustomer=${unitCustomer}&`

    url += `family=${family}&isRetail=${isRetail}`

    return http.get(url).then((response) => response.bitolas)
  }

  static getProductPackages(codeCustomer, unitCustomer, family, bitola, isRetail) {

    let url = `product-packages?codeCustomer=${codeCustomer}&unitCustomer=${unitCustomer}&`

    url += `family=${family}&bitola=${bitola}&isRetail=${isRetail}`

    return http.get(url)
  }

  static getAttachmentsCategories() {
    return http.get("attachments-categories").then((response) => response.categories)
  }

  static getSubjectsCategories() {
    return http.get("subjects-categories").then((response) => response.subjects)
  }

  static getProductPrice(data) {
    return http.post("quotations/get-product-price", data)
  }

  static getPriceFromRg(data) {
    return http.post("quotations/get-price-from-rg", data).then((response) => response.price)
  }

  static search(data) {
    return http.post("quotations/search", data).then((response) => {

      let quotations = response.quotations

      quotations.forEach((quotation) => quotation.statusData = QuotationService.QUOTATION_STATUS[quotation.status])

      return quotations
    })
  }

  static create(quotation) {
    return http.post("quotations/create", quotation).then((response) => response.id)
  }

  static finishMaintenance(quotationId) {
    return http.post("quotations/finish-maintenance", { quotationId })
  }

  static takeApproval(quotationId) {
    return http.post("quotations/take-approval", { quotationId })
  }

  static giveupApproval(quotationId) {
    return http.post("quotations/giveup-approval", { quotationId })
  }

  static approve(quotationId) {
    return http.post("quotations/approve", { quotationId })
  }

  static activateCanceled(quotationId) {
    return http.post("quotations/activatecanceled", { quotationId })
  }

  static disapprove(quotationId) {
    return http.post("quotations/disapprove", { quotationId })
  }

  static undoApproval(quotationId) {
    return http.post("quotations/undo-approval", { quotationId })
  }

  static cancel(data) {
    return http.post("quotations/cancel", data)
  }

  static lockUpdate(quotationId) {
    return http.post("quotations/lock-update", { quotationId })
  }

  static unlockUpdate(quotationId) {
    return http.post("quotations/unlock-update", { quotationId })
  }

  static requestTecnicalApproval(quotationId) {
    return http.post("quotations/request-tecnical-approval", { quotationId })
  } 
  
  static confirm(data) {
    return http.post("quotations/confirm", data)
  }
   

  static print(id, revision) {

    let url = `quotations/print/${id}`
    let fileName = "orcamento_" + id

    if (revision) {
      url += `?revision=${revision}`    
      fileName += `_revisao_${revision}`
    }  

    fileName += ".pdf"  

    return http({ url, method: "GET", responseType: "blob" })
      .then((data) => {
        
        let blob = new Blob([data], { type: "application/pdf" })
        let url = window.URL.createObjectURL(blob)
        let link = document.createElement("a")

        document.body.appendChild(link)

        link.href = url
        link.download = fileName
        link.click()        
    })  
  }
  
  static batchApproval(data) {
    return http.post("quotations/batch-approval", data)
  }

  static batchPriceUpdate(data) {
    return http.post("quotations/batch-price-update", data)
  }

  static validItem(item, codeCustomer, unitCustomer) {
    const payload = { ...item }
    if (codeCustomer) payload.codeCustomer = codeCustomer
    if (unitCustomer) payload.unitCustomer = unitCustomer
    return http.post("quotations/valid-item", payload)
  }

  static update(data) {
    return http.put("quotations", data)
  }

  static updateCustomer(data) {
    return http.put("quotations/update-customer", data)
  }

  static updateAppPrice(data) {
    return http.put("quotations/update-app-price", data)
  }

  static delete(id) {
    return http.delete(`quotations/${id}`)
  }

  static showQuotation(id, revision) {
    let cliurl = config.client.url

    if (window.location.toString().toLowerCase().includes(config.client.bkp)) {
      cliurl = config.client.urlBkp
    }

    let url = cliurl + `/quotations/show/${id}?closeTab=true`

    if (revision)
      url += `&revision=${revision}`
    
    AppBrowser.openNewTab(url)
  }

  static showDifQuotation(id, revision) {
    let cliurl = config.client.url

    if (window.location.toString().toLowerCase().includes(config.client.bkp)) {
      cliurl = config.client.urlBkp
    }

    let url = cliurl + `/quotations/showdif/${id}/${revision}`
  
    AppBrowser.openNewTab(url)
  }

  static updateStore(quotation) {

    if (!store.state.quotations)
      return

    let quotations = store.state.quotations.slice()
    let filter = store.state.quotationsFilter    
    let newQuotations = []  

    quotations.forEach((q) => {      

      if (q.id == quotation.id) {
        q.status = quotation.status
        q.statusData = QuotationService.QUOTATION_STATUS[quotation.status]
        q.nameCustomer = quotation.nameCustomer
        q.appTotal = quotation.appTotal
        q.sugTotal = quotation.sugTotal
      }
      
      if (!filter.status || q.status == filter.status)
        newQuotations.push(q)
    })  

    store.commit("setQuotations", newQuotations)
  }

  static removeFromStore(quotationId) {

    if (!store.state.quotations)
      return

    let quotations = store.state.quotations.slice()
    let newQuotations = []  

    quotations.forEach((quotation) => {
      if (quotation.id !== quotationId)
        newQuotations.push(quotation)      
    })  

    store.commit("setQuotations", newQuotations)
  }
}

QuotationService.QUOTATION_STATUS = {
  "0": { description: "Revisão", icon: "camera", color: "grey", rgb: colors.grey },
  "1": { description: "Em manutenção", icon: "edit", color: "black", rgb: colors.grey },
  "2": { description: "Aguardando aprovação", icon: "clock", color: "orange", rgb: colors.orange },
  "3": { description: "Em aprovação", icon: "users", color: "blue", rgb: colors.blue },
  "4": { description: "Aguardando confirmação", icon: "clock", color: "teal", rgb: colors.teal },
  "5": { description: "Confirmado", icon: "check circle", color: "green", rgb: colors.green },
  "6": { description: "Não aprovado", icon: "thumbs down", color: "red", rgb: colors.red },
  "7": { description: "Cancelado", icon: "times", color: "red", rgb: colors.red },
  "8": { description: "Em aprovação técnica", icon: "cogs", color: "brown", rgb: colors.brown },
  "9": { description: "Aguardando processamento", icon: "clock", color: "yellow", rgb: colors.yellow },
  "A": { description: "Em processamento", icon: "spinner", color: "blue", rgb: colors.blue },
  "B": { description: "Erro de processamento", icon: "exclamation circle", color: "red", rgb: colors.red },
  "C": { description: "Rejeição técnica", icon: "unlink circle", color: "red", rgb: colors.red },
}

function formatInteger(value) {
  return numeral(value).format("0,0")
}

function compareRev(AntRev, lastRev) {
  let ret = lastRev
  let temp = null
  const dif = diff(AntRev, lastRev)

  //json-diff nao trabalha de forma correta com array dentro de json
  Object.keys(dif).forEach((key) => {
    Object.keys(lastRev).forEach((key1) => {
      if (key == key1) {
        if (Array.isArray(ret[key1])) {
          Object.keys(ret[key1]).forEach((itemA) => {
            Object.keys(ret[key1][itemA]).forEach((itemB) => {
              if (Array.isArray(ret[key1][itemA][itemB])) {
                Object.keys(ret[key1][itemA][itemB]).forEach((itemC) => {
                  if (Array.isArray(ret[key1][itemA][itemB][itemC])) {
                    console.log('Ajustar Quotation Service')
                  } else {
                    if (!AntRev[key1][itemA] || !lastRev[key1][itemA]) {
                      return
                    }
                    temp = diff(AntRev[key1][itemA][itemB][itemC], lastRev[key1][itemA][itemB][itemC])
                    ret[key1][itemA][itemB][itemC] = temp ? temp : ret[key1][itemA][itemB][itemC]
                  }
                })
              } else {
                if (!AntRev[key1][itemA] || !lastRev[key1][itemA]) {
                  return
                }
                temp = diff(AntRev[key1][itemA][itemB], lastRev[key1][itemA][itemB])
                if (temp) {
                  Object.keys(temp).forEach((it) => {
                    if (typeof temp[it] == 'object' && temp[it] != null) {
                      ret[key1][itemA][itemB][it] = temp[it] ? temp[it] : ret[key1][itemA][itemB][it]
                    } else {
                      ret[key1][itemA][itemB] = temp ? temp : ret[key1][itemA][itemB]
                    }
                  })
                }
              }
            }) 
          })
        } else {
          ret[key1] = dif[key]
        }
      }
    })
  })

  return ret
}